import * as React from 'react';
import * as intl from 'react-intl-universal';

import {
    LeftOutlined,
    MinusCircleOutlined,
    PlusCircleOutlined,
    PlusOutlined,
    RightOutlined,
    SaveOutlined,
    SyncOutlined,
    UploadOutlined,
} from '@ant-design/icons';

import { Col, message, Modal, Row, Upload } from 'antd';
import Button from 'common/components/Button';
import ReactCropper from 'react-cropper';
import Loading from 'common/components/Loading';
import utils from 'common/utils/upload';
import { upload } from 'pc-sales/utils/http';
import 'cropperjs/dist/cropper.css';
import './style.less';

export default class Cropper extends React.Component<any> {
    src: any;
    image: any;
    timeout: any;
    locales: any = {};
    showCropper: boolean = true;
    quality: number = 1;
    state: any = {
        lastSrc: '',
        src: '',
        preview: '',
        fileList: [],
        file: null,
        lock: false
    };

    /**
     * 关闭模态框
     */
    closeClickHandler = () => {
        const { id, onTrigger } = this.props;
        onTrigger("closeModal", id);
    };

    /**
     * 上传/压缩
     * @param file
     * @returns {Promise<void>}
     */
    upload = async (file: any) => {
        try {
            const data = await upload('/v2/common/file/upload', { file });
            await this.props.config.onOK(data);
            this.closeClickHandler();
        } catch (err) {
            this.setState({ lock: false });
        }
    };

    /**
     * 裁剪保存
     */
    cropSaveHandler = () => {
        if (this.image) {
            const { file } = this.state;
            const preview = this.image.getCroppedCanvas().toDataURL(file ? file.type : 'image/png', this.quality);
            this.setState({ preview });
        }
    };

    /**
     * 获取压缩参数
     * @param file
     * @returns {any}
     */
    getQuality = (file: any): any => {
        const mb = 1024 * 1024;
        // 根据图片大小设置压缩系数
        if (Math.max(0, file.size, 3 * mb) == (3 * mb)) {
            return 1;
        } else if (Math.max(3 * mb, file.size, 15 * mb) == (15 * mb)) {
            return 0.6;
        } else if (Math.max(15 * mb, file.size, 30 * mb) == (30 * mb)) {
            return 0.3;
        }
    };

    /**
     * 保存提交
     */
    cropSubmitHandler = () => {
        if (this.state.lock) return;
        try {
            this.setState({ lock: true });
            const { preview, file } = this.state;
            utils.imagePress({ file: file || {}, src: preview, quality: this.getQuality(file || { size: preview.length }) }, this.upload);
        } catch (err) {
            this.setState({ lock: false });
        }
    };

    /**
     * 删除文件
     * @param file
     */
    onRemove = (file: any) => {
        this.setState(({ fileList }: any) => {
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            return {
                fileList: newFileList,
            };
        });
    };

    /**
     * 上传前处理
     * @param file
     * @returns {boolean}
     */
    beforeUpload = (file: any) => {
        if (file.type.split('/')[0] !== 'image') {
            message.error(this.locales.type);
        } else if (file.size  > 30 * 1024 * 1024) {
            message.error(this.locales.size);
        } else {
            // 设置压缩系数
            this.quality = file.size > 512 * 512 ? 0.2 : 1;
            // 读取文件
            const reader = new FileReader();
            reader.onloadend = () => {
                const src = reader.result;
                this.setState({ src, lastSrc: src, preview: src, file });
            };
            reader.readAsDataURL(file);
        }
        return false;
    };

    /**
     * 图像旋转
     * @param {1 | -1} direction
     * @returns {() => void}
     */
    rotateHandler = (direction: 1 | -1) => () => {
        let rotate = 45 * direction;
        this.image.cropper && this.image.cropper.rotate(rotate);
        this.cropSaveHandler();
    };

    /**
     * 图像放大（X,Y轴同时放大）
     * @param {number} multiple
     * @returns {() => void}
     */
    scaleHandler = (multiple: number) => () => {
        if (this.image) {
            const { scaleX, scaleY } = this.image.cropper.getImageData();
            this.image.cropper.scale(multiple * scaleX || 1, multiple * scaleY || 1);
            this.cropSaveHandler();
        }
    };

    /**
     * 显示裁剪框
     */
    showCropHandler = () => {
        if (this.image) {
            let showCropper = !this.showCropper;
            this.showCropper = showCropper;
            showCropper ? this.image.cropper.crop() : this.image.cropper.clear();
        }
    };

    /**
     * 裁剪重置
     */
    resetHandler = () => {
        let src = this.state.lastSrc || this.src;
        this.image && this.image.cropper.reset();
        this.setState({ src, preview: src });
    };

    /**
     * 变化响应
     */
    cropHandler = (e: any) => {
        if (e.detail.width) {
            if (this.timeout) {
                clearTimeout(this.timeout);
                this.timeout = null;
            }
            this.timeout = setTimeout(() => {
                this.cropSaveHandler();
            }, 300);
        }
    };

    componentDidMount() {
        const { lang, config } = this.props;
        const { data } = config;
        this.locales = utils.getLocales(lang);
        this.setState({ src: data, preview: data });
    }

    render() {
        const { lock, src, fileList, preview } = this.state;
        return (
            <Modal title={intl.get('Component.CropperImageEdit')}
                   visible={true}
                   width={900}
                   onCancel={this.closeClickHandler}
                   footer={null}
                   maskClosable={false}>
                <Loading enabled={lock}>
                    <Row gutter={30}>
                        <Col span={19}>
                            <ReactCropper ref={(ctx: any) => this.image = ctx} src={src}
                                crop={this.cropHandler}
                                aspectRatio={1}
                                checkOrientation={false}
                                style={{ height: 400, width: '100%', overflow: 'hidden' }}
                                guides={true}
                                autoCropArea={1}
                            />
                        </Col>
                        <Col span={5}>
                            <div id='preview' className='Cropper-preview' style={{ marginBottom: 15 }}>
                                <img src={preview} style={{ width: '100%' }}/>
                            </div>
                            <Upload action={'/file/upload'} fileList={fileList} onRemove={this.onRemove}
                                    beforeUpload={this.beforeUpload}>
                                <Button type='primary'
                                        icon={<UploadOutlined />}>{intl.get('Component.CropperUploadImage')}</Button>
                            </Upload>
                        </Col>
                    </Row>
                    <Row gutter={30}>
                        <Col span={20}>
                            <div className='Cropper-btn-group clearfix'>
                                <div className='pull-left'>
                                    <Button type='primary' icon={<LeftOutlined />}
                                            onClick={this.rotateHandler(-1)}>{intl.get('Component.CropperRotateToLeft')}</Button>
                                    <Button type='primary' icon={<RightOutlined />}
                                            onClick={this.rotateHandler(1)}>{intl.get('Component.CropperRotateToRight')}</Button>
                                </div>
                                <div className="pull-right">
                                    <Button type='primary' icon={<PlusOutlined />} onClick={this.showCropHandler}/>
                                    <Button type='primary' icon={<PlusCircleOutlined />} onClick={this.scaleHandler(1.2)}/>
                                    <Button type='primary' icon={<MinusCircleOutlined />} onClick={this.scaleHandler(0.8)}/>
                                    <Button type='primary' icon={<SyncOutlined />} onClick={this.resetHandler}/>
                                </div>
                            </div>
                        </Col>
                        <Col span={4}>
                            <div className='Cropper-btn-group clearfix'>
                                <Button style={{ width: '100%' }} type='primary' onClick={this.cropSubmitHandler}
                                        icon={<SaveOutlined />}>
                                    {intl.get('Global.Submit')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Loading>
            </Modal>
        );
    }
}
